package team.platforma.multiplatform_material_extension.calendar.calendar_period.core_ui

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.AnimationEndReason
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.indication
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerInputChange
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn
import team.platforma.multiplatform_material_extension.calendar.core.core_ui.CalendarBadge
import team.platforma.multiplatform_material_extension.calendar.style.CalendarViewStyle
import team.platforma.multiplatform_material_extension.calendar.utils.AnimatedMonthChange
import team.platforma.multiplatform_material_extension.calendar.utils.AnimatedMonthChangeListener
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarBadges
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarBadgesListener
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarRange
import team.platforma.multiplatform_material_extension.calendar.utils.daysInMonth
import team.platforma.multiplatform_material_extension.util.gestures.HorizontalGestures
import team.platforma.multiplatform_material_extension.util.gestures.HorizontalGesturesListener
import kotlin.math.abs

@Composable
internal fun CalendarPeriodDatesView(
    initFirstDate: LocalDate,
    initSecondDate: LocalDate,
    year: Int,
    month: Int,
    onPeriodSelected: (firstDate: LocalDate?, secondDate: LocalDate?) -> Unit,
    calendarRange: CalendarRange,
    onPreviousMonth: () -> Unit,
    onNextMonth: () -> Unit,
    adaptiveHeight: Boolean,
    calendarBadges: CalendarBadges?,
    horizontalGestures: HorizontalGestures?,
    animatedMonthChange: AnimatedMonthChange?,
    animateBadge: Boolean,
    clearPeriod: Boolean,
    onPeriodCleared: () -> Unit
) {

    val daysInMonth = daysInMonth(year, month)
    val firstDayOfWeek = LocalDate(year, month, 1).dayOfWeek.isoDayNumber

    var rows = 6

    if (adaptiveHeight) {
        val totalCells = (daysInMonth + firstDayOfWeek - 1)
        rows = if (totalCells % 7 == 0) {
            totalCells / 7
        } else {
            val lastRowStartIndex = totalCells - (totalCells % 7)
            val hasDaysInLastRow = (lastRowStartIndex until totalCells).any { index ->
                val dayOfMonth = index - firstDayOfWeek + 1
                dayOfMonth in 1..daysInMonth
            }
            totalCells / 7 + if (hasDaysInLastRow) 1 else 0
        }
    }


    var mFirstCheckedDate by remember { mutableStateOf<LocalDate?>(initFirstDate) }
    var mSecondCheckedDate by remember { mutableStateOf<LocalDate?>(initSecondDate) }

    if (clearPeriod) {
        mFirstCheckedDate = null
        mSecondCheckedDate = null
        onPeriodCleared()
    }

    LaunchedEffect(mFirstCheckedDate, mSecondCheckedDate) {
        if (mFirstCheckedDate != null && mSecondCheckedDate != null) {
            if (mSecondCheckedDate!! < mFirstCheckedDate!!) {
                val secondDate = LocalDate(
                    mSecondCheckedDate!!.year,
                    mSecondCheckedDate!!.monthNumber,
                    mSecondCheckedDate!!.dayOfMonth
                )
                val firstDate = LocalDate(
                    mFirstCheckedDate!!.year,
                    mFirstCheckedDate!!.monthNumber,
                    mFirstCheckedDate!!.dayOfMonth
                )
                mSecondCheckedDate = firstDate
                mFirstCheckedDate = secondDate
            }
            if (mSecondCheckedDate!! > mFirstCheckedDate!!) {
                onPeriodSelected(
                    mFirstCheckedDate!!, mSecondCheckedDate!!
                )
            }
        } else {
            onPeriodSelected(null, null)
        }
    }

    var dragOffset by remember { mutableStateOf(Animatable(0f)) }
    var reversAnimation by remember { mutableStateOf(false) }

    val coroutineScope = rememberCoroutineScope()

    DisposableEffect(horizontalGestures) {

        val listener = object : HorizontalGesturesListener {

            var width = 1f
            var offset = 0f
            var endDrag = false

            override fun width(float: Float) {
                width = float
            }

            override fun onDragStart(offset: Offset) {
                reversAnimation = false
            }

            override fun onHorizontalDrag(change: PointerInputChange, dragAmount: Float) {

                if (!endDrag) {
                    offset += dragAmount
                    dragOffset = Animatable(((abs(offset)) / width).coerceIn(0f, 1f))

                    val threshold = width / 2
                    if (offset > threshold) {
                        onPreviousMonth()
                        endDrag = true
                        offset = 0f
                    } else if (offset < -threshold) {
                        onNextMonth()
                        endDrag = true
                        offset = 0f
                    }
                }
            }

            override fun onDragCancel() {
                offset = 0f
                endDrag = false
                reversAnimation = true
            }

            override fun onDragEnd() {
                offset = 0f
                endDrag = false
                reversAnimation = true
            }
        }

        horizontalGestures?.addListener(listener)

        onDispose {
            horizontalGestures?.removeListener(listener)
        }
    }

    if (reversAnimation) {
        coroutineScope.launch {
            dragOffset.animateTo(
                targetValue = 0f, animationSpec = tween(durationMillis = 250)
            )
        }
    }

    DisposableEffect(animatedMonthChange) {
        val listener = object : AnimatedMonthChangeListener {
            override fun fadeout(animationEnd: () -> Unit) {
                coroutineScope.launch {
                    val animation = dragOffset.animateTo(
                        targetValue = 0.5f, animationSpec = tween(durationMillis = 100)
                    )
                    when (animation.endReason) {
                        AnimationEndReason.BoundReached -> {}
                        AnimationEndReason.Finished -> {
                            animationEnd.invoke()
                            reversAnimation = true
                        }
                    }
                }
            }


        }
        animatedMonthChange?.addListener(listener)
        onDispose {
            animatedMonthChange?.removeListener(listener)
        }
    }


    Column {

//        Spacer(modifier = Modifier.height(8.dp))


        Box(modifier = Modifier.graphicsLayer {
            scaleX = 1f - 0.2f * dragOffset.value
            scaleY = 1f - 0.2f * dragOffset.value
            alpha = 1f - 1f * (2f * dragOffset.value)
        }

        ) {


            CalendarDatesContent(
                rows,
                firstDayOfWeek,
                daysInMonth,
                year,
                month,
                calendarRange,
                firstCheckedDate = mFirstCheckedDate,
                secondCheckedDate = mSecondCheckedDate,
                onDateSelected = { checkedDate ->

                    when {
                        mFirstCheckedDate != null && checkedDate == mFirstCheckedDate -> {
                            mFirstCheckedDate = null
                        }

                        mFirstCheckedDate == null && checkedDate != mSecondCheckedDate -> {
                            mFirstCheckedDate = checkedDate
                        }

                        mSecondCheckedDate != null && checkedDate == mSecondCheckedDate -> {
                            mSecondCheckedDate = null
                        }

                        mSecondCheckedDate == null && checkedDate != mFirstCheckedDate -> {
                            mSecondCheckedDate = checkedDate
                        }

                        mFirstCheckedDate == null && mSecondCheckedDate != null && checkedDate != mSecondCheckedDate -> {
                            mFirstCheckedDate = checkedDate
                        }

                        mSecondCheckedDate == null && mFirstCheckedDate != null && checkedDate != mFirstCheckedDate -> {
                            mSecondCheckedDate = checkedDate
                        }

                        mFirstCheckedDate != null && checkedDate < mFirstCheckedDate!! -> {
                            mFirstCheckedDate = checkedDate
                        }

                        mSecondCheckedDate != null && mFirstCheckedDate != null && checkedDate > mFirstCheckedDate!! && checkedDate != mSecondCheckedDate -> {
                            mSecondCheckedDate = checkedDate
                        }
                    }
                },
                calendarBadges = calendarBadges,
                coroutineScope = coroutineScope,
                animateBadge = animateBadge
            )
        }

    }
}

@Composable
private fun CalendarDatesContent(
    rows: Int,
    firstDayOfWeek: Int,
    daysInMonth: Int,
    year: Int,
    month: Int,
    calendarRange: CalendarRange,
    firstCheckedDate: LocalDate?,
    secondCheckedDate: LocalDate?,
    onDateSelected: (LocalDate) -> Unit,
    calendarBadges: CalendarBadges?,
    coroutineScope: CoroutineScope,
    animateBadge: Boolean
) {

    var badgesMap by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }

    val dateNow: LocalDate = Clock.System.todayIn(TimeZone.currentSystemDefault())

    DisposableEffect(calendarBadges) {
        val listener = object : CalendarBadgesListener {
            override fun onSetBadgeMap(map: Map<String, Int>) {
                badgesMap = badgesMap + map
            }

        }
        calendarBadges?.addListener(listener)

        onDispose {
            calendarBadges?.removeListener(listener)
        }
    }

    var maxWidth by remember { mutableStateOf(0f) }
    var boxWidth by remember { mutableStateOf(0f) }
    val density = LocalDensity.current

    val cellSize = with(density) { maxWidth.toDp() / 7 }

    Column(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.fillMaxWidth()
            .onGloballyPositioned { layoutCoordinates ->
                maxWidth = layoutCoordinates.size.width.toFloat()
            }
    ) {

        for (row in 0 until rows) {
            Row(
                modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center
            ) {

                for (col in 0..6) {
                    val dayIndex = row * 7 + col
                    val dayOfMonth = dayIndex - firstDayOfWeek + 2

                    Box(
                        modifier = Modifier.size(cellSize),
                        contentAlignment = Alignment.Center
                    ) {
                        if (dayOfMonth in 1..daysInMonth) {

                            val currentDate = LocalDate(year, month, dayOfMonth)

                            val enabled = currentDate in calendarRange.max..calendarRange.min

                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {

                                Box(
                                    modifier = Modifier.fillMaxSize()
                                        .setPeriodStyle(
                                            visibleDate = currentDate,
                                            firstDate = firstCheckedDate,
                                            secondDate = secondCheckedDate
                                        )
                                )

                                Box(
                                    modifier = Modifier.fillMaxSize()
                                        .padding(
                                            horizontal = CalendarViewStyle.dateStyle().cellPadding.horizontal,
                                            vertical = CalendarViewStyle.dateStyle().cellPadding.vertical
                                        )
                                        .border(
                                            width = CalendarViewStyle.dateStyle().borderWidth,
                                            color = if (currentDate == dateNow) CalendarViewStyle.dateStyle().currentDateBorder else CalendarViewStyle.dateStyle().defaultDateBorder,
                                            shape = CalendarViewStyle.dateStyle().shape
                                        )
                                )

                                Box(modifier = Modifier.fillMaxSize()
                                    .indication(interactionSource = remember { MutableInteractionSource() }, indication = null)
                                    .padding(
                                        horizontal = CalendarViewStyle.dateStyle().cellPadding.horizontal,
                                        vertical = CalendarViewStyle.dateStyle().cellPadding.vertical
                                    )
                                    .background(
                                        color = if (currentDate == firstCheckedDate || currentDate == secondCheckedDate) CalendarViewStyle.dateStyle().checkedDateBackground else CalendarViewStyle.dateStyle().defaultDateBackground,
                                        shape = CalendarViewStyle.dateStyle().shape
                                    ).clip(CalendarViewStyle.dateStyle().shape).border(
                                        width = CalendarViewStyle.dateStyle().borderWidth,
                                        color = if (currentDate == firstCheckedDate || currentDate == secondCheckedDate) CalendarViewStyle.dateStyle().checkedDateBorder else CalendarViewStyle.dateStyle().defaultDateBorder,
                                        shape = CalendarViewStyle.dateStyle().shape
                                    ).clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null,
                                        enabled = enabled
                                    ) {
                                        onDateSelected(currentDate)
                                    },
                                ) {
                                    Box(
                                        Modifier.fillMaxSize()
                                            .onGloballyPositioned { layoutCoordinates ->
                                                boxWidth = layoutCoordinates.size.width.toFloat()
                                            }
                                    )
                                }

                                Text(
                                    text = "$dayOfMonth", color = if (enabled) {
                                        if (currentDate == firstCheckedDate || currentDate == secondCheckedDate) {
                                            CalendarViewStyle.dateStyle().checkedDateText
                                        } else if (firstCheckedDate != null && secondCheckedDate != null) {
                                            if (currentDate > firstCheckedDate && currentDate < secondCheckedDate) {
                                                CalendarViewStyle.dateStyle().periodDateText
                                            } else {
                                                CalendarViewStyle.dateStyle().enabledDateText
                                            }
                                        } else {
                                            CalendarViewStyle.dateStyle().enabledDateText
                                        }
                                    } else {
                                        CalendarViewStyle.dateStyle().disableDateText
                                    }, style = TextStyle(
                                        fontSize = (cellSize.value * CalendarViewStyle.dateStyle().fontSize).sp,
                                        fontFamily = CalendarViewStyle.dateStyle().fontFamily,
                                        fontWeight = CalendarViewStyle.dateStyle().fontWeight,
                                        textAlign = TextAlign.Center,
                                        lineHeight = TextUnit.Unspecified,
                                        letterSpacing = 0.sp
                                    ), maxLines = 1
                                )

                                CalendarBadge(
                                    value = badgesMap[currentDate.toString()] ?: 0,
                                    cellSize = with(density) { boxWidth.toDp() }
                                )

                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun Modifier.setPeriodStyle(
    visibleDate: LocalDate, firstDate: LocalDate?, secondDate: LocalDate?
): Modifier {
    if (firstDate != null && secondDate != null) {
        if (visibleDate in firstDate..secondDate) {
            if (visibleDate == firstDate) {
                return this.padding(
                    start = CalendarViewStyle.periodStyle().startPeriod.padding.start,
                    top = CalendarViewStyle.periodStyle().startPeriod.padding.top,
                    bottom = CalendarViewStyle.periodStyle().startPeriod.padding.bottom,
                    end = CalendarViewStyle.periodStyle().startPeriod.padding.end
                ).background(
                    color = CalendarViewStyle.periodStyle().startPeriod.color,
                    shape = CalendarViewStyle.periodStyle().startPeriod.shape
                )

            } else if (visibleDate == secondDate) {
                return this.padding(
                    start = CalendarViewStyle.periodStyle().endPeriod.padding.start,
                    top = CalendarViewStyle.periodStyle().endPeriod.padding.top,
                    bottom = CalendarViewStyle.periodStyle().endPeriod.padding.bottom,
                    end = CalendarViewStyle.periodStyle().endPeriod.padding.end
                ).background(
                    color = CalendarViewStyle.periodStyle().endPeriod.color,
                    shape = CalendarViewStyle.periodStyle().endPeriod.shape
                )
            } else if (visibleDate.minus(DatePeriod(days = 1)) == firstDate) {
                return this.padding(
                    start = CalendarViewStyle.periodStyle().trackPeriod.startPadding.start,
                    top = CalendarViewStyle.periodStyle().trackPeriod.startPadding.top,
                    bottom = CalendarViewStyle.periodStyle().trackPeriod.startPadding.bottom,
                    end = CalendarViewStyle.periodStyle().trackPeriod.startPadding.end
                ).background(
                    color = CalendarViewStyle.periodStyle().trackPeriod.startColor,
                    shape = CalendarViewStyle.periodStyle().trackPeriod.startShape
                )
            } else if (visibleDate.plus(DatePeriod(days = 1)) == secondDate) {
                return this.padding(
                    start = CalendarViewStyle.periodStyle().trackPeriod.endPadding.start,
                    top = CalendarViewStyle.periodStyle().trackPeriod.endPadding.top,
                    bottom = CalendarViewStyle.periodStyle().trackPeriod.endPadding.bottom,
                    end = CalendarViewStyle.periodStyle().trackPeriod.endPadding.end
                ).background(
                    color = CalendarViewStyle.periodStyle().trackPeriod.endColor,
                    shape = CalendarViewStyle.periodStyle().trackPeriod.endShape
                )
            } else {
                return this.padding(
                    start = CalendarViewStyle.periodStyle().trackPeriod.padding.start,
                    top = CalendarViewStyle.periodStyle().trackPeriod.padding.top,
                    bottom = CalendarViewStyle.periodStyle().trackPeriod.padding.bottom,
                    end = CalendarViewStyle.periodStyle().trackPeriod.padding.end
                ).background(
                    color = CalendarViewStyle.periodStyle().trackPeriod.color,
                    shape = CalendarViewStyle.periodStyle().trackPeriod.shape
                )
            }

        } else {
            return this.background(Color.Transparent)
        }
    } else {
        return this.background(Color.Transparent)
    }
}


//@Composable
//private fun CalendarBadge(
//    value: Int,
//    cellSize: Dp
//) {
//    val minBadgeSize = cellSize / 3
//    Box(
//        modifier = Modifier.fillMaxSize().padding(
//            horizontal = CalendarViewStyle.dateStyle().cellPadding.horizontal,
//            vertical = CalendarViewStyle.dateStyle().cellPadding.vertical
//        ), contentAlignment = Alignment.TopEnd
//    ) {
//        AnimatedVisibility(
//            visible = value > 0, enter = fadeIn() + scaleIn(), exit = fadeOut() + scaleOut()
//        ) {
//            Box(
//                modifier = Modifier.wrapContentWidth().wrapContentSize().background(
//                    color = CalendarViewStyle.badgeStyle().background,
//                    shape = CalendarViewStyle.badgeStyle().shape
//                ), contentAlignment = Alignment.Center
//            ) {
//
//                Box(
//                    modifier = Modifier.widthIn(min = minBadgeSize).heightIn(min = minBadgeSize)
//                        .padding(2.dp), contentAlignment = Alignment.Center
//                ) {
//                    Text(
//                        text = when {
//                            value > 99 -> "99+"
//                            value == 0 -> ""
//                            else -> value.toString()
//                        },
//                        color = CalendarViewStyle.badgeStyle().textColor,
//                        style = TextStyle(
//                            fontSize = (minBadgeSize.value * CalendarViewStyle.badgeStyle().fontSize).sp,
//                            fontWeight = CalendarViewStyle.badgeStyle().fontWeight,
//                            textAlign = TextAlign.Center,
//                            lineHeight = TextUnit.Unspecified,
//                            letterSpacing = 0.sp
//                        ),
//                        maxLines = 1
//                    )
//                }
//            }
//        }
//    }
//}
